import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addFurtherInvestigation,
  editComplaint,
  editInvestigation2,
} from "services/prosecutorDecipilineService";
import { AdminUnit, Complaints, Investigation } from "types";
import { LoadingIcon } from "utils/constant";
import {
  COMPLIANT_STATUS,
  FEEDBACK_TYPE,
  INVESTIGATION_STATUS,
} from "utils/constantValues";
import "../styles/AddEvidenceModal.css";

interface FurtherInvestigationModalProps {
  adminUnitData: AdminUnit;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: Complaints;
  investigationData: Investigation;
  onRefresh: any;
}

export default function AddFurtherInvestigationModal({
  adminUnitData,
  isOpen,
  setIsOpen,
  complaintData,
  investigationData,
  onRefresh,
}: FurtherInvestigationModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState<string | null>(null);
  // const [success, setSuccess] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formState, setFormState] = useState<Investigation>({
    feedback_status: FEEDBACK_TYPE?.APPROVED,
    feedback: "",
    complaint: null,
    status: INVESTIGATION_STATUS?.UNDER_INVESTIGATION,
    files: [],
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    // setError(null);
    // setSuccess(false);

    if (!investigationData) return;

    const data = new FormData();
    data.append(
      "further_investigation_documents",
      (formState.files ?? [])[0] || "",
    );
    data.append("comment", formState.feedback || "");
    data.append("investigation", investigationData?.uu_id || "");

    try {
      const resData = await addFurtherInvestigation(data);

      if (resData) {
        await editComplaint({
          ...complaintData,
          status: COMPLIANT_STATUS.UNDER_INVESTIGATION,
          admin_unit_id:
            adminUnitData?.parent_unit || complaintData.admin_unit_id,
        });

        await editInvestigation2(
          {
            feedback_status: FEEDBACK_TYPE?.REVISE,
          },
          investigationData?.uu_id || "",
          false,
        );
      }

      navigate(-1);

      onRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      // setSuccess(true);
      setFormState({
        feedback_status: FEEDBACK_TYPE?.APPROVED,
        feedback: "",
        complaint: null,
        status: INVESTIGATION_STATUS?.UNDER_INVESTIGATION,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
      // setError(
      //   err?.response?.data?.message || err.message || "Something went wrong."
      // );
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      files: (prev.files ?? []).filter((_, i) => i !== index),
    }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [
          ...(prev.files ?? []),
          ...Array.from(e.target.files as FileList),
        ],
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("addFurtherInvestigation")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("feedback")}
            </label>
            <textarea
              required
              rows={7}
              name="feedback"
              value={formState.feedback}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        {/* Supporting Documents Section */}
        <section className="space-y-4">
          <div className="flex items-center">
            <div className="h-5 w-1 bg-red-700 rounded-r mr-3"></div>
            <h3 className="text-base font-semibold text-gray-800">
              {t("supportingDocuments")}
            </h3>
          </div>

          <div className="pl-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("uploadOfficialDecisionDocument")}
            </label>

            {/* File Upload Area */}
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-400 hover:bg-red-50 transition-colors cursor-pointer"
              onClick={triggerFileInput}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                style={{ display: "none" }}
              />
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mb-3">
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {t("uploadSupportingDocuments")}
                </p>
                <p className="text-xs text-gray-500">
                  {t("fileTypesWithMax10mb")}
                </p>
              </div>
            </div>

            {/* File List */}
            {(formState?.files?.length ?? 0) > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  {t("selectedDocuments")} ({formState.files?.length ?? 0})
                </p>
                <div className="space-y-2">
                  {(formState.files ?? []).map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2.5"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded">
                          <span className="text-xs font-medium text-red-700">
                            {file.type
                              .split("/")[1]
                              ?.substring(0, 3)
                              .toUpperCase() || "DOC"}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800 truncate max-w-xs">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800 mt-8"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("addFurtherInvestigation")
          )}
        </button>
      </form>
    </SideModal>
  );
}
